import React, {useCallback, useEffect, useMemo, useState} from "react";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import {Box, Button, Card, CircularProgress, Skeleton, ToggleButton, toggleButtonClasses, ToggleButtonGroup, Tooltip, Typography} from "@mui/material";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Unstable_Grid2";
import {useConfigureNotebook} from "../../../../../hooks/use-notebook-config";
import {routes} from "../../../../../../core/models/static/routes/app-routes";
import {Link} from "react-router-dom";
import useBehavioralAnalyticsTool from "../../../../../hooks/use-behavioural-analytics-tool";

const ProductSharingRulesView = () => {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const isMounted = useIsMounted();
    const {configureNotebookTags} = useConfigureNotebook('product-sharing-rules');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();

    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     * - Configures the behavioral analytics tool for the view
     */
    useEffect(() => {
        configureNotebookTags((noteTags) => noteTags.productsShareRules);
        configureBehavioralTool((tags) => tags.productsShareRules);
    }, [configureBehavioralTool, configureNotebookTags]);

    /**
     * Fetches the entries in the system.
     */
    const getProductShareRules = useCallback(async () => {
        setLoading(true);
        // TODO: call api to fetch the product share rule policies.
        const accessLevels = [
            {
                title: 'Public',
                description: 'By marking a part as public, all of your partner and customer will have access to all the code types',
                id: 1,
            },
            {
                title: 'Private',
                description: 'code will not be shared for user or customer',
                id: 2,
            },
        ]
        const response = {
            resultFlag: true,
            data: [
                {
                    title: 'Description',
                    id: 1,
                    availableAccessLevels: accessLevels,
                    accessLevelId: 1,
                    description: 'The description of the product',
                },
                {
                    title: "Tags",
                    id: 2,
                    availableAccessLevels: accessLevels,
                    accessLevelId: 2,
                    description: "The tags of the product"
                },
                {
                    title: "Property Groups",
                    id: 3,
                    availableAccessLevels: accessLevels,
                    accessLevelId: 1,
                    description: "The property groups of the product"
                },
                {
                    title: "Images",
                    id: 4,
                    availableAccessLevels: accessLevels,
                    accessLevelId: 1,
                    description: "The images of the product",
                },
                {
                    title: "Prices",
                    id: 5,
                    availableAccessLevels: accessLevels,
                    accessLevelId: 2,
                    description: "The prices of the product",
                },
                {
                    title: "Technical Information",
                    id: 6,
                    availableAccessLevels: accessLevels,
                    accessLevelId: 1,
                    description: "The technical information of the product",
                },
                {
                    title: "Public Notes",
                    id: 7,
                    availableAccessLevels: accessLevels,
                    accessLevelId: 1,
                    description: "The public notes of the product",
                },
                {
                    title: "Private Notes",
                    id: 8,
                    availableAccessLevels: accessLevels,
                    accessLevelId: 2,
                    description: "The private notes of the product",
                },
                {
                    title: "Relations",
                    id: 9,
                    availableAccessLevels: accessLevels,
                    accessLevelId: 1,
                    description: "The relations of the product",
                },
            ]
        }
        if (!isMounted())
            return;
        setLoading(false);
        if (response?.resultFlag) {
            setData([
                ...response.data ?? [],
                {
                    title: 'Code Types',
                    id: -1,
                    custom: true,
                    navigateTo: routes.products.codeTypes,
                    actionTitle: 'Manage Access Levels',
                    description: 'The groups of codes assigned to the products in this system. These codes are used to make them easier to find in the system',
                },
            ]);
        }
    }, [isMounted, setData]);

    /**
     * As soon as the dialog opens:
     * - fetches the list of entries
     */
    useEffect(() => {
        getProductShareRules().then();
    }, [getProductShareRules]);

    /**
     * Updates the shared rule of an entry
     * @type {(function(*, *): Promise<void>)}
     */
    const updateAccessLevel = useCallback(async (entry, accessLevelId) => {
        // TODO: call api to update the product share rule access level.
        await new Promise(r => setTimeout(r, 2000));
        const response = {
            resultFlag: true,
        }
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setData(prevState => prevState.map(e => e.id === entry.id ? {...e, accessLevelId: accessLevelId} : e));
        }
    }, [setData, isMounted])

    return (
        <>
            <Box sx={{display: 'flex', flexDirection: 'column', marginBlockEnd: 4}}>
                <Typography variant={'subtitle1'} fontWeight={600}>
                    Product Sharing Rules
                </Typography>
                <Typography variant={'caption'} color={'text.secondary'}>
                    In this section, you can view and manage what sections / parts of your own products will be shared with your partners,
                    when they become your partners.
                </Typography>
            </Box>
            <Paper sx={{padding: 2, display: 'flex', gap: 2, flexDirection: 'column'}}>
                {
                    !loading &&
                    data.map((entry) => (
                        <ShareRuleCard
                            key={entry.id}
                            entry={entry}
                            updateAccessLevel={updateAccessLevel}
                        />
                    ))
                }
                {
                    loading &&
                    <Box sx={{display: 'flex', flexDirection: 'column', gap: 2, marginBlock: 1}}>
                        {
                            Array.from({length: 4}).map((_, key) => (
                                <Skeleton
                                    component={'div'}
                                    key={key}
                                    width={'100%'}
                                    height={146}
                                    sx={{transformOrigin: 'none', transform: 'none'}}
                                />
                            ))
                        }
                    </Box>
                }
                {
                    !loading && data.length < 1 &&
                    <Typography variant={'caption'} color={'text.secondary'} textAlign={'center'} sx={{marginBlock: 6, width: '100%'}}>
                        No code types found.
                    </Typography>
                }
            </Paper>
        </>
    );
}

const ShareRuleCard = ({entry, updateAccessLevel}) => {
    const [updatingAccessLevel, setUpdatingAccessLevel] = useState(false);
    const [newAccessLevelId, setNewAccessLevelId] = useState();
    const isMounted = useIsMounted()

    const toggleButtonOptions = useMemo(() =>
        entry.custom
            ? []
            : entry.availableAccessLevels.map((accessLevel) => ({
                title: accessLevel.title,
                value: accessLevel.id,
                description: accessLevel.description,
            })), [entry.custom, entry.availableAccessLevels]);

    /**
     * Updates the shared rule of the entry.
     * @type {(function(*, *): Promise<void>)}
     */
    const onAccessLevelSelected = useCallback(async (e, accessLevelId) => {
        if (!accessLevelId)
            return;
        setNewAccessLevelId(accessLevelId);
        setUpdatingAccessLevel(true);
        await updateAccessLevel(entry, accessLevelId);
        if (!isMounted())
            return;
        setUpdatingAccessLevel(false);
        setNewAccessLevelId(null);
    }, [entry, isMounted, updateAccessLevel])

    return (
        <>
            <Card variant={'outlined'} sx={{padding: 1}}>
                <Grid container spacing={2} marginInline={0} marginBlock={0}>
                    <Grid xs>
                        <Box sx={{display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: 0.5, height: '100%'}}>
                            <Typography variant={'body2'} fontWeight={600}>
                                {entry.title ?? ""}
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid xs={'auto'}>
                        {
                            entry.custom &&
                            <>
                                <Button
                                    variant={'text'}
                                    color={'primary'}
                                    size={'small'}
                                    component={Link}
                                    to={entry.navigateTo}
                                >
                                    {entry.actionTitle ?? ""}
                                </Button>
                            </>
                        }
                        {
                            !entry.custom &&
                            <ToggleButtonGroup
                                orientation="horizontal"
                                disabled={updatingAccessLevel}
                                value={newAccessLevelId ?? entry.shareRuleId}
                                onChange={onAccessLevelSelected}
                                exclusive
                                size={'small'}
                                sx={{
                                    [`.${toggleButtonClasses.root}`]: {
                                        fontWeight: 600,
                                        padding: '0.4rem 1rem',
                                    }
                                }}
                            >
                                {
                                    toggleButtonOptions.map((option) => (
                                        <ToggleButton key={option.value} value={option.value}>
                                            <Tooltip title={option.description} disableInteractive>
                                                <Typography
                                                    variant={'body2'}
                                                    fontWeight={600}
                                                    component={'div'}
                                                    sx={{display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5}}
                                                >
                                                    {
                                                        newAccessLevelId === option.value &&
                                                        <CircularProgress size={16}/>
                                                    }
                                                    {option.title}
                                                </Typography>
                                            </Tooltip>
                                        </ToggleButton>
                                    ))
                                }
                            </ToggleButtonGroup>
                        }
                    </Grid>
                    <Grid xs={12} sx={{padding: 0,}}/>
                    <Grid xs>
                        <Typography variant={'body2'}>
                            {entry.description ?? ""}
                        </Typography>
                    </Grid>
                </Grid>
            </Card>
        </>
    )
}

export default ProductSharingRulesView;
